// components/affiliateGap/gapCard.jsx - Gap Analysis Brand Card with Redux polling (same as Dashboard)
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedBrand } from '../../store/slices/liveAffiliatesSlice';
import {
  startGapPollingWithCleanup,
  stopGapPollingWithCleanup,
  selectGapBrandProcessingStatus,
  cleanupGapProcessingBrands
} from '../../store/slices/gapBrandsSlice';
import { Globe, ExternalLink, Users, Activity, TrendingUp, TrendingDown, CheckCircle, XCircle, Clock, BarChart3, GitCompare, Lightbulb } from 'lucide-react';
import FaviconImage from '../common/FaviconImage';
import BrandUpdateBadge, { BrandUpdateProgress } from '../common/BrandUpdateBadge';
import { useBrandUpdate } from '../../services/brandUpdates';

const GapCard = ({ brand, onStatusUpdate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mountedRef = useRef(true);

  // Get processing status from Redux store using selector
  const processingInfo = useSelector(selectGapBrandProcessingStatus(brand.brand_url));

  // Use Redux store status if available, otherwise fall back to brand prop
  const currentProcessingStatus = processingInfo?.status || brand.processing_status;

  // Check if brand is currently processing (1-7 are processing, 8 is completed)
  const isProcessing = currentProcessingStatus &&
    parseInt(currentProcessingStatus) >= 1 &&
    parseInt(currentProcessingStatus) < 8 &&
    currentProcessingStatus !== 'completed' &&
    currentProcessingStatus !== 'failed';

  const isCompleted = currentProcessingStatus === 'completed' || parseInt(currentProcessingStatus) === 8;
  const isFailed = currentProcessingStatus === 'failed';

  // Live-affiliate verification ("bulb"): analysis is done but we're still
  // confirming in the background which affiliates are really live.
  const liveCheckState = (processingInfo?.isActive && processingInfo?.liveCheckStatus) || brand.live_check_status;
  const isLiveChecking = liveCheckState === 'pending' || liveCheckState === 'running';

  // Someone is re-running this competitor's domain — usually the admin from
  // Update Brand. The gap is measured against a shared affiliate list, so the
  // Common/Gap numbers on this card are being rebuilt whether or not this
  // account started the run. When it ends, refetch to pick up the new counts.
  const brandUpdate = useBrandUpdate(brand.brand_url || brand.url, () => {
    if (onStatusUpdate) onStatusUpdate(brand.id, 'completed');
  });
  const showUpdateBadge = Boolean(brandUpdate) && !isProcessing && !isFailed;

  // Check for current month data
  const hasCurrentMonthData = brand.current_month_gained !== undefined || brand.current_month_lost !== undefined;
  const gainedCount = brand.current_month_gained || 0;
  const lostCount = brand.current_month_lost || 0;

  // Start polling if the brand is processing OR its background live-affiliate
  // check is still running (e.g. after a page refresh) — so the bulb clears
  // and counts refresh in real time in both cases.
  useEffect(() => {
    if ((isProcessing || isLiveChecking) && !processingInfo?.isActive) {
      dispatch(startGapPollingWithCleanup({
        brandUrl: brand.brand_url,
        brandName: brand.brand_name || brand.name,
        brandId: brand.id,
        initialStatus: brand.processing_status
      }));
    }

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      if (processingInfo?.isActive) {
        dispatch(stopGapPollingWithCleanup(brand.brand_url));
      }
    };
  }, [isProcessing, isLiveChecking, processingInfo?.isActive, brand.brand_url, brand.brand_name, brand.name, brand.id, brand.processing_status, dispatch]);

  // Stop polling when processing completes or fails — but NOT while the
  // background live-affiliate check is still running, so the bulb clears in
  // real time and the final counts land without a page refresh.
  // Fire-once ref: the reducer may deactivate polling in the same action that
  // marks completion, so we can't gate the parent refresh on isActive.
  const completionNotifiedRef = useRef(false);

  // Did THIS card instance actually watch the work happen? processingBrands
  // keeps an entry after polling stops, so a card mounting later sees a
  // finished-looking entry it never observed finish. Without this it would
  // announce a completion that already happened, the parent would refetch, and
  // the refetch would remount the card — announcing it again, forever.
  const observedWorkRef = useRef(false);
  useEffect(() => {
    if (isProcessing || isLiveChecking) observedWorkRef.current = true;
  }, [isProcessing, isLiveChecking]);

  useEffect(() => {
    const finished = (isCompleted && !isLiveChecking) || isFailed;
    if (finished && processingInfo && observedWorkRef.current && !completionNotifiedRef.current) {
      completionNotifiedRef.current = true;

      if (processingInfo.isActive) {
        dispatch(stopGapPollingWithCleanup(brand.brand_url));
      }

      // Notify parent component to refresh brands
      if (onStatusUpdate) {
        onStatusUpdate(brand.id, isCompleted ? 'completed' : 'failed');
      }
    }
  }, [processingInfo, isCompleted, isFailed, isLiveChecking, brand.brand_url, brand.id, currentProcessingStatus, dispatch, onStatusUpdate]);

  // Cleanup old processing records periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      dispatch(cleanupGapProcessingBrands());
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(cleanupInterval);
  }, [dispatch]);

  // Gap Affiliates click handler - navigate to live affiliates with gap filter
  // Allow clicking even during processing (status >= 1) so users can see data as it becomes available
  const handleGapAffiliatesClick = () => {
    if (isFailed) return;

    // Gap = the competitor's affiliates that your brand does NOT have. The card's
    // own brand IS the competitor (the "Competitor Website URL" field is saved to
    // brand_url); your dashboard brand is user_brand_url. These two were the wrong
    // way round, so the drill-down listed your own affiliates instead of the
    // opportunities — matching the equally inverted count on the card.
    // The list is built FROM the competitor and EXCLUDES your brand.
    dispatch(setSelectedBrand({
      brand_url: brand.brand_url || brand.url,
      brand_name: brand.brand_name || brand.name || brand.brand_url,
      brand_id: brand.id,
      country: brand.country,
      vertical: brand.vertical,
      isGapAnalysis: true,
      isCommonAffiliates: false,
      userBrandUrl: brand.user_brand_url
    }));
    navigate('/live-affiliate');
  };

  // Common Affiliates click handler - navigate to live affiliates with common filter
  // Allow clicking even during processing (status >= 1) so users can see data as it becomes available
  const handleCommonAffiliatesClick = () => {
    if (isFailed) return;

    // Common = affiliates shared by both brands (symmetric, so the count is the
    // same either way). Base on the competitor (brand_url) and require your brand
    // (user_brand_url) too, matching the gap drill-down's orientation.
    dispatch(setSelectedBrand({
      brand_url: brand.brand_url || brand.url,
      brand_name: brand.brand_name || brand.name || brand.brand_url,
      brand_id: brand.id,
      country: brand.country,
      vertical: brand.vertical,
      isGapAnalysis: false,
      isCommonAffiliates: true,
      userBrandUrl: brand.user_brand_url
    }));
    navigate('/live-affiliate');
  };

  const getProcessingDisplay = () => {
    const status = currentProcessingStatus;
    const step = processingInfo?.step;

    if (!status || (parseInt(status) < 1 && status !== 'completed' && status !== 'failed')) return null;

    // User-friendly status messages (non-technical)
    const statusMessages = {
      '1': 'Analyzing brand',
      '2': 'Gathering data',
      '3': 'Finding opportunities',
      '4': 'Identifying partners',
      '5': 'Building insights',
      '6': 'Analyzing trends',
      '7': 'Preparing report',
      '8': 'Finalizing results',
      'completed': 'Analysis complete',
      'failed': 'Processing failed'
    };

    const message = step || statusMessages[status] || 'Processing';
    const stepNumber = parseInt(status) || (status === 'completed' ? 8 : (status === 'failed' ? 0 : 1));
    // V2 pipelines report an exact percent via the poll; legacy falls back to steps
    const exactPercent = (typeof processingInfo?.percent === 'number') ? processingInfo.percent : null;

    return {
      message,
      stepNumber,
      progress: status === 'completed' ? 100 : (status === 'failed' ? 0 : (exactPercent ?? (stepNumber / 8) * 100)),
      percentLabel: exactPercent !== null ? `${Math.round(exactPercent)}%` : `${stepNumber}/8`,
      status
    };
  };

  const processingDisplay = getProcessingDisplay();

  // Metrics - Domain Rank, Common Affiliates and Gap Affiliates (unique ones)
  // Show actual counts even during processing so users can see data as it becomes available
  const metrics = [
    {
      title: 'Domain Rank',
      value: brand.rank?.toLocaleString() || (isProcessing ? '...' : 'N/A'),
      icon: BarChart3,
      onClick: null,
      showGainedLost: false
    },
    {
      title: 'Common Affiliates',
      value: brand.common_affiliates || 0,
      icon: Users,
      onClick: handleCommonAffiliatesClick,
      showGainedLost: false
    },
    {
      title: 'Gap Affiliates',
      value: brand.gap_analysis_affiliates || 0,
      icon: Activity,
      onClick: handleGapAffiliatesClick,
      showGainedLost: true
    }
  ];

  const handleExternalLinkClick = (e) => {
    e.stopPropagation();
    window.open(`https://${brand.brand_url}`, '_blank');
  };

  return (
    <div className={`bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 transition-all duration-300 group relative overflow-hidden transform ${
      isProcessing ? 'border-blue-300 dark:border-blue-600 shadow-sm' : 'hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm hover:-translate-y-0.5'
    }`}>
      <div className="relative p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex items-center gap-2">
              <FaviconImage
                domain={brand.brand_url || brand.url}
                size={28}
                className="rounded-md"
                faviconUrl={brand.favicon_url}
              />
              {isProcessing && (
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              )}
            </div>
            <div>
              <h3 className="text-base font-medium text-blue-600 dark:text-blue-400 mb-0.5">
                {brand.brand_name || brand.name}
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
                  <Globe className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  <span>{brand.brand_url || brand.url}</span>
                  <ExternalLink
                    className="w-3 h-3 cursor-pointer text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                    onClick={handleExternalLinkClick}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Status Indicator only */}
          <div className="flex items-center gap-2">
            {/* Processing Status Indicator */}
            {isProcessing && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg animate-pulse">
                <Clock className="w-3 h-3 text-blue-600 dark:text-blue-400 animate-spin" />
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                  {processingDisplay?.percentLabel || 'Processing'}
                </span>
              </div>
            )}

            {/* A refresh is running against this domain — started here or by
                the admin from Update Brand; either way these numbers move. */}
            {showUpdateBadge && <BrandUpdateBadge update={brandUpdate} compact />}

            {/* Live-affiliate check bulb: analysis is ready, background
                verification of which affiliates are really live is running */}
            {isLiveChecking && !isProcessing && !isFailed && (
              <div
                className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg"
                title="Analysis is ready! We're still confirming which affiliates actively promote this brand, so the gap numbers may update shortly."
              >
                <Lightbulb className="w-3 h-3 text-amber-500 animate-pulse" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Verifying live affiliates</span>
              </div>
            )}

            {/* Completed Indicator */}
            {isCompleted && !isProcessing && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
                <span className="text-xs font-medium text-green-700 dark:text-green-300">Completed</span>
              </div>
            )}

            {/* Failed Indicator */}
            {isFailed && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                <XCircle className="w-3 h-3 text-red-600 dark:text-red-400" />
                <span className="text-xs font-medium text-red-700 dark:text-red-300">Failed</span>
              </div>
            )}
          </div>
        </div>

        {/* Which stage the shared refresh is on. Only when this card has no
            progress bar of its own — two bars would say the same thing twice. */}
        {showUpdateBadge && <BrandUpdateProgress update={brandUpdate} />}

        {/* Processing Progress Bar - Same as Dashboard */}
        {isProcessing && processingDisplay && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                {processingDisplay.message}...
              </span>
              <span className="text-xs text-blue-500 dark:text-blue-300">
                {processingDisplay.percentLabel}
              </span>
            </div>
            <div className="w-full bg-blue-100 dark:bg-blue-900/30 rounded-full h-1.5">
              <div
                className="bg-blue-600 dark:bg-blue-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${processingDisplay.progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Failed Status Message */}
        {isFailed && (
          <div className="mb-4 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
            <div className="text-xs text-red-700 dark:text-red-300">
              Processing failed. Please try adding the brand again.
            </div>
          </div>
        )}

        {/* Compared With Section */}
        {brand.user_brand_url && (
          <div className="mb-4 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
            <div className="flex items-center gap-2">
              <GitCompare className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span className="text-xs text-amber-700 dark:text-amber-300">
                <span className="font-medium">{brand.brand_name || brand.name}</span>
                <span className="mx-1">compared with</span>
                <span className="font-medium text-amber-800 dark:text-amber-200">{brand.user_brand_url}</span>
              </span>
            </div>
          </div>
        )}

        <div className="h-px bg-gray-200 dark:bg-slate-700 mb-4"></div>

        {/* Metrics Grid - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {metrics.map((metric, metricIndex) => (
            <div
              key={metricIndex}
              className={`bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-3 transition-all duration-300 ${
                metric.onClick && !isFailed
                  ? 'cursor-pointer transform hover:scale-105 hover:shadow-sm hover:bg-gray-100 dark:hover:bg-slate-700'
                  : ''
              } ${isFailed ? 'opacity-75' : ''}`}
              onClick={!isFailed ? metric.onClick : undefined}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <metric.icon className={`w-4 h-4 ${
                  isFailed ? 'text-gray-400 dark:text-slate-500' : 'text-blue-600 dark:text-blue-400'
                }`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-gray-700 dark:text-slate-200">
                      {isFailed && metric.title === 'Gap Affiliates'
                        ? 'Failed'
                        : metric.value
                      }
                    </div>

                    {/* Gained/Lost arrows for Gap Affiliates */}
                    {metric.showGainedLost && hasCurrentMonthData && !isFailed && (
                      <div className="flex items-center gap-1">
                        {gainedCount > 0 && (
                          <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 rounded text-xs">
                            <TrendingUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                            <span className="text-green-700 dark:text-green-300 font-medium">{gainedCount}</span>
                          </div>
                        )}
                        {lostCount > 0 && (
                          <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 rounded text-xs">
                            <TrendingDown className="w-3 h-3 text-red-600 dark:text-red-400" />
                            <span className="text-red-700 dark:text-red-300 font-medium">{lostCount}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-xs font-medium text-gray-500 dark:text-slate-400">
                {metric.title}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GapCard;
